import { Button } from "@/components/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/card";
import { AlertTriangle, Bug, RefreshCw } from "lucide-react";
import { ReactNode } from "react";
import { FallbackProps, ErrorBoundary as ReactErrorBoundary } from "react-error-boundary";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: { componentStack: string }) => void;
}

function ErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  const handleReload = () => {
    window.location.reload();
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 h-16 w-16 text-destructive">
            <AlertTriangle className="h-full w-full" />
          </div>
          <CardTitle className="text-2xl">Something went wrong</CardTitle>
          <CardDescription className="text-base">
            An unexpected error occurred in the application. We apologize for the inconvenience.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
            <div className="flex items-start space-x-3">
              <Bug className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-destructive">{error.name || "Error"}</p>
                <p className="text-sm text-destructive/80 mt-1 break-words">
                  {error.message || "An unknown error occurred"}
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button onClick={resetErrorBoundary} variant="default" className="flex-1">
              <RefreshCw className="mr-2 h-4 w-4" />
              Try Again
            </Button>
            <Button onClick={handleReload} variant="outline" className="flex-1">
              Reload Page
            </Button>
          </div>

          <div className="text-center text-sm text-muted-foreground">
            <p>
              If this problem persists, please contact us at{" "}
              <a
                href="https://remerge.slack.com/archives/C0372TQ8C78"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline"
              >
                #ua-dev-team
              </a>{" "}
              with the error details above.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function ErrorBoundary({ children, fallback, onError }: ErrorBoundaryProps) {
  const handleError = (error: Error, errorInfo: { componentStack: string }) => {
    console.error("Error caught by boundary:", error, errorInfo);
    onError?.(error, errorInfo);
  };

  return (
    <ReactErrorBoundary FallbackComponent={fallback ? () => <>{fallback}</> : ErrorFallback} onError={handleError}>
      {children}
    </ReactErrorBoundary>
  );
}
